import React, { useContext } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Button, Checkbox } from "@mui/material";
import axios from "../../axios";
import { DataContext } from "../../App";
import MiniLoading from "../MiniLoading";
import SetPasswordDialog from "./SetPasswordDialog";
import Auth from "../../Auth";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Row = ({ data, handleOpenPasswordDialog }) => {
  const [canOrder, setCanOrder] = React.useState(data.CanOrder);
  const [isAdmin, setIsAdmin] = React.useState(data.IsAdmin);
  const [sendOrderConfirmation, setSendOrderConfirmation] = React.useState(data.send_order_confirmation ?? false);
  const [isInApi, setIsInApi] = React.useState(false);
  const { setSnackbar } = useContext(DataContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const changeCanOrder = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setCanOrder((prevState) => {
      setIsInApi(true);

      axios
        .put(
          "/admin/accounts/" + e.target.getAttribute("data-id") + "/can-order",
          { can_order: !prevState }
        )
        .catch(() => {
          setCanOrder(data.CanOrder);
        })
        .finally(() => {
          setIsInApi(false);
        });

      return !prevState;
    });
  };

  const changeSendOrderConfirmation = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setSendOrderConfirmation((prevState) => {
      setIsInApi(true);

      axios
        .put(
          "/admin/accounts/" + e.target.getAttribute("data-id") + "/send-order-confirmation",
          { send_order_confirmation: !prevState }
        )
        .catch(() => {
          setSendOrderConfirmation(data.send_order_confirmation ?? false);
        })
        .finally(() => {
          setIsInApi(false);
        });

      return !prevState;
    });
  };

  const changeIsAdmin = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setIsAdmin((prevState) => {
      setIsInApi(true);

      axios
        .put(
          "/admin/accounts/" + e.target.getAttribute("data-id") + "/is-admin",
          { is_admin: !prevState }
        )
        .catch(() => {
          setIsAdmin(data.IsAdmin);
        })
        .finally(() => {
          setIsInApi(false);
        });

      return !prevState;
    });
  };

  const sendPasswordMail = (e) => {
    let sure = window.confirm(
      "Password mail will be send for " +
      e.target.getAttribute("data-email") +
      ", are you sure?"
    );

    if (sure) {
      setIsInApi(true);

      axios
        .post(
          "/admin/accounts/" +
          e.target.getAttribute("data-id") +
          "/send-password-link"
        )
        .then((response) => {
          setIsInApi(false);

          setSnackbar({
            message: t("Successfully sent password mail."),
            severity: "success",
            open: true,
          });
        })
        .catch((err) => {
          setSnackbar({
            message: t("Error while sending password mail."),
            severity: "error",
            open: true,
          });
        })
        .finally(() => {
          setIsInApi(false);
        });
    }
  };

  const handleImpersonate = (e) => {
    const accountId = e.target.getAttribute("data-id");
    const accountName = e.target.getAttribute("data-name");

    setIsInApi(true);

    axios
      .post(`admin/accounts/${accountId}/impersonate`)
      .then((response) => {
        if (response.data.success) {
          Auth.setToken(response.data.data.token);
          Auth.setUser(response.data.data.user);
          Auth.startImpersonation(response.data.data);

          setSnackbar({
            message: `Je bent nu ingelogd als ${accountName}`,
            severity: "success",
            open: true,
          });

          navigate("/");
          window.location.reload();
        }
      })
      .catch((err) => {
        setSnackbar({
          message: err.response?.data?.message || "Fout bij het impersoneren van deze klant.",
          severity: "error",
          open: true,
        });
      })
      .finally(() => {
        setIsInApi(false);
      });
  };

  return (
    <TableRow
      key={data.ID}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
      }}
    >
      <TableCell component="th" scope="row" style={{ whiteSpace: 'nowrap' }}>
        {data.Code}
      </TableCell>
      <TableCell style={{ whiteSpace: 'nowrap' }}>{data.Name}</TableCell>
      <TableCell style={{ whiteSpace: 'nowrap' }}>{data.Email}</TableCell>
      <TableCell>
        <Checkbox
          checked={canOrder}
          inputProps={{ "data-id": data.ID }}
          onChange={changeCanOrder}
          disabled={isInApi}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={sendOrderConfirmation}
          inputProps={{ "data-id": data.ID }}
          onChange={changeSendOrderConfirmation}
          disabled={isInApi}
          color="primary"
        />
      </TableCell>
      {/* <TableCell>
        <Checkbox
          color="apimasters"
          checked={isAdmin}
          inputProps={{
            "data-id": data.ID,
          }}
          onChange={changeIsAdmin}
          disabled={isInApi}
        />
      </TableCell> */}
      <TableCell style={{ whiteSpace: 'nowrap' }}>
        <Button
          color="apimasters"
          variant="contained"
          data-id={data.ID}
          data-email={data.Email}
          onClick={sendPasswordMail}
          size="small"
          sx={{ mr: 1 }}
          disabled={
            isInApi ||
            (data.Email != null && data.Email.trim() === "") ||
            data.Email == null
          }
        >
          {t("SendPasswordMail")}
        </Button>
        <Button
          color="apimasters"
          variant="contained"
          data-id={data.ID}
          data-name={data.Name + " (" + data.Code.trim() + ")"}
          onClick={handleOpenPasswordDialog}
          disabled={isInApi}
          size="small"
          sx={{ mr: 1 }}
        >
          {t("SetPassword")}
        </Button>
        <Button
          color="warning"
          variant="contained"
          data-id={data.ID}
          data-name={data.Name + " (" + data.Code.trim() + ")"}
          onClick={handleImpersonate}
          disabled={isInApi || !canOrder}
          size="small"
          sx={{ minWidth: '100px' }}
        >
          Login als
        </Button>
      </TableCell>
      <TableCell style={{ fontSize: '0.8rem', color: '#555', whiteSpace: 'nowrap' }}>
        {data.last_order_date
          ? new Date(data.last_order_date).toLocaleDateString("nl-NL")
          : <span style={{ color: '#bbb' }}>—</span>}
      </TableCell>
      <TableCell style={{ fontSize: '0.8rem', textAlign: 'center' }}>
        {data.order_count > 0
          ? <span style={{ fontWeight: 600, color: '#1565c0' }}>{data.order_count}</span>
          : <span style={{ color: '#bbb' }}>0</span>}
      </TableCell>
    </TableRow>
  );
};

export default function AccountsTable(props) {
  const [open, setOpen] = React.useState(false);
  const [selectedAccount, setSelectedAccount] = React.useState(null);
  const { t } = useTranslation();

  const handleOpenPasswordDialog = (e) => {
    setSelectedAccount({
      id: e.target.getAttribute("data-id"),
      name: e.target.getAttribute("data-name"),
    });

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };


  const Alert = (props) => (
    <TableRow>
      <TableCell colSpan={8} sx={{ textAlign: "center" }}>
        {props.message}
      </TableCell>
    </TableRow>
  );

  return (
    <>
      {open && (
        <SetPasswordDialog
          handleClose={handleClose}
          selectedAccount={selectedAccount}
        />
      )}
      <TableContainer component={Paper} style={{ overflowX: 'auto', width: '100%' }}>
        <Table style={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ whiteSpace: 'nowrap' }}>{t("Code")}</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>{t("Name")}</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>{t("Email")}</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>{t("CanOrder")}</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>{t("Order Confirmation")}</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>{t("Actions")}</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>{t("Last Order")}</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap', textAlign: 'center' }}># Orders</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data == null ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <MiniLoading />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {Auth.getUser().store_division == null ? <Alert message={t("First please do auth at Authentication page.")} /> :
                  props.data.data.length === 0 && <Alert message={t("There are no accounts.")} />}
                {Auth.getUser().store_division != null
                  && props.data.data?.map((row) => (
                    <Row
                      key={row.ID}
                      data={row}
                      handleOpenPasswordDialog={handleOpenPasswordDialog}
                    />
                  ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
